import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { SubmitHandler } from 'react-hook-form';

import { TicketAttachmentFormValuesT } from '../declarations';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import {
  useCreateTicketAttachment,
  useCreateTicketMultiAttachment,
  useCreateTicketMultiPartAttachmentComplete,
  useCreateTicketMultiPartAttachmentInitiate,
  useCreateTicketMultiPartAttachmentUpload,
} from 'hooks/api/useTickets';

type UseAddAttachmentPopupT = {
  handleSubmitForm: SubmitHandler<TicketAttachmentFormValuesT>;
  isCreatingAttachment: boolean;
  isAttachmentCreationError: boolean;
  createAttachmentErrorMessage: string | undefined;
  createAttachmentValidationErrorMessage: string | undefined;
  isAttachmentSuccessfullyPublished: boolean;
  showAttachmentsForm: boolean;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

type UseAddAttachmentPopupParamsT = {
  ticketId: string;
  categoryName: string;
  assignToWarrCloud: boolean;
};

const useAddAttachmentPopup = ({
  ticketId,
  categoryName,
  assignToWarrCloud,
}: UseAddAttachmentPopupParamsT): UseAddAttachmentPopupT => {
  /**
   * Hooks
   */
  const queryClient = useQueryClient();

  /**
   * Api hooks
   */
  const createAttachmentMutation = useCreateTicketAttachment({
    ticketId,
    onSuccess: (_data, variables, _context) => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (variables.AssignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      }
    },
  });

  const createMultiAttachmentMutation = useCreateTicketMultiAttachment({
    ticketId,
    onSuccess: (_data, variables, _context) => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);

      if (variables[0].AssignToOppositeUser) {
        queryClient.invalidateQueries([RequestQueryKeysE.FetchTicket]);
      }
    },
  });

  const createMultiPartAttachmentInitiateMutation = useCreateTicketMultiPartAttachmentInitiate({
    ticketId,
    onSuccess: (_data, _context) => {},
  });

  const createMultiPartAttachmentUploadMutation = useCreateTicketMultiPartAttachmentUpload({
    onSuccess: (_data, _context) => {},
  });

  const createMultiPartAttachmentCompleteMutation = useCreateTicketMultiPartAttachmentComplete({
    ticketId,
    onSuccess: (_data, _context) => {
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketMessages]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTicketAttachments]);
      queryClient.invalidateQueries([RequestQueryKeysE.FetchTickets]);
    },
  });

  /**
   * Variables
   */
  const createAttachmentErrorMessage = useMemo(
    () => getErrorMessage(createAttachmentMutation.error),
    [createAttachmentMutation.error]
  );
  const createAttachmentValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createAttachmentMutation.error),
    [createAttachmentMutation.error]
  );

  const createMultiPartAttachmentInitiateErrorMessage = useMemo(
    () => getErrorMessage(createMultiPartAttachmentInitiateMutation.error),
    [createMultiPartAttachmentInitiateMutation.error]
  );
  const createMultiPartAttachmentInitiateValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createMultiPartAttachmentInitiateMutation.error),
    [createMultiPartAttachmentInitiateMutation.error]
  );

  const createMultiPartAttachmentUploadErrorMessage = useMemo(
    () => getErrorMessage(createMultiPartAttachmentUploadMutation.error),
    [createMultiPartAttachmentUploadMutation.error]
  );
  const createMultiPartAttachmentUploadValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createMultiPartAttachmentUploadMutation.error),
    [createMultiPartAttachmentUploadMutation.error]
  );

  const createMultiPartAttachmentCompleteErrorMessage = useMemo(
    () => getErrorMessage(createMultiPartAttachmentCompleteMutation.error),
    [createMultiPartAttachmentCompleteMutation.error]
  );
  const createMultiPartAttachmentCompleteValidationErrorMessage = useMemo(
    () => getValidationErrorMessage(createMultiPartAttachmentCompleteMutation.error),
    [createMultiPartAttachmentCompleteMutation.error]
  );

  const createChunks = (file: File, cSize: number) => {
    let startPointer = 0;
    let endPointer = file.size;
    let chunks = [];
    while (startPointer < endPointer) {
      let newStartPointer = startPointer + cSize;
      chunks.push(file.slice(startPointer, newStartPointer));
      startPointer = newStartPointer;
    }
    return chunks;
  };

  async function blobToBase64Async(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.onerror = e => reject(fileReader.error);
      fileReader.onloadend = e => {
        const dataUrl = fileReader.result as string;
        const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
        resolve(base64);
      };
      fileReader.readAsDataURL(blob);
    });
  }

  /**
   * Handlers
   */
  const resetCreateAttachmentMutationState = useCallback(() => {
    if (createAttachmentMutation.isSuccess || createAttachmentMutation.isError) {
      createAttachmentMutation.reset();
    }
  }, [createAttachmentMutation]);
  const resetCreateMultiAttachmentMutationState = useCallback(() => {
    if (createMultiAttachmentMutation.isSuccess || createMultiAttachmentMutation.isError) {
      createMultiAttachmentMutation.reset();
    }
  }, [createMultiAttachmentMutation]);

  const resetCreateMultiPartAttachmentInitiateMutationState = useCallback(() => {
    if (
      createMultiPartAttachmentInitiateMutation.isSuccess ||
      createMultiPartAttachmentInitiateMutation.isError
    ) {
      createMultiPartAttachmentInitiateMutation.reset();
    }
  }, [createMultiPartAttachmentInitiateMutation]);

  const resetCreateMultiPartAttachmentCompleteMutationState = useCallback(() => {
    if (
      createMultiPartAttachmentCompleteMutation.isSuccess ||
      createMultiPartAttachmentCompleteMutation.isError
    ) {
      createMultiPartAttachmentCompleteMutation.reset();
    }
  }, [createMultiPartAttachmentCompleteMutation]);

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
      resetCreateAttachmentMutationState();
      resetCreateMultiPartAttachmentCompleteMutationState();
      resetCreateMultiAttachmentMutationState();
      resetCreateMultiPartAttachmentInitiateMutationState();
    },
    [
      resetCreateAttachmentMutationState,
      resetCreateMultiAttachmentMutationState,
      resetCreateMultiPartAttachmentInitiateMutationState,
      resetCreateMultiPartAttachmentCompleteMutationState,
    ]
  );

  const handleSubmitForm = useCallback(
    async (data: TicketAttachmentFormValuesT) => {
      const attachmentPhoto = data.attachmentPhoto || [];
      const attachmentFile = data.attachmentFile || [];

      const attachmentPhotoArray = Array.from(attachmentPhoto) as File[];
      const attachmentFileArray = Array.from(attachmentFile) as File[];

      const allAttachments = [...attachmentPhotoArray, ...attachmentFileArray];

      for (const attachment of allAttachments) {
        if (attachment.size < 10000000) {
          await createAttachmentMutation.mutateAsync({
            'Attachment.File': attachment,
            'Attachment.AttachmentTypeCode': categoryName,
            'Attachment.Notes': data.note || categoryName,
            AssignToOppositeUser: assignToWarrCloud,
          });

          continue;
        }

        var initiateResponse = await createMultiPartAttachmentInitiateMutation.mutateAsync({
          attachmentTypeCode: categoryName,
          fileContentType: attachment.type,
        });

        var chunks = createChunks(attachment, 6000000);
        var partCounter = 1;
        var partNumberETags = [];

        for (var chunk of chunks) {
          const content = await blobToBase64Async(chunk);

          var uploadResponse = await createMultiPartAttachmentUploadMutation.mutateAsync({
            fileKey: initiateResponse.fileKey,
            uploadId: initiateResponse.uploadId,
            partNumber: partCounter,
            content: content,
          });

          partCounter++;
          partNumberETags.push(uploadResponse);
        }

        await createMultiPartAttachmentCompleteMutation.mutateAsync({
          fileKey: initiateResponse.fileKey,
          uploadId: initiateResponse.uploadId,
          partNumberETags: partNumberETags,
          attachmentTypeCode: categoryName,
          notes: data.note || categoryName,
          assignToOppositeUser: assignToWarrCloud,
          originalFileName: attachment.name,
        });
      }
    },
    [
      categoryName,
      assignToWarrCloud,
      createAttachmentMutation,
      createMultiPartAttachmentInitiateMutation,
      createMultiPartAttachmentUploadMutation,
      createMultiPartAttachmentCompleteMutation,
    ]
  );

  return {
    handleSubmitForm,
    isAttachmentCreationError:
      createAttachmentMutation.isError ||
      createMultiPartAttachmentCompleteMutation.isError ||
      createMultiPartAttachmentInitiateMutation.isError ||
      createMultiPartAttachmentUploadMutation.isError,
    createAttachmentErrorMessage:
      createAttachmentErrorMessage ||
      createMultiPartAttachmentInitiateErrorMessage ||
      createMultiPartAttachmentUploadErrorMessage ||
      createMultiPartAttachmentCompleteErrorMessage,
    createAttachmentValidationErrorMessage:
      createAttachmentValidationErrorMessage ||
      createMultiPartAttachmentInitiateValidationErrorMessage ||
      createMultiPartAttachmentUploadValidationErrorMessage ||
      createMultiPartAttachmentCompleteValidationErrorMessage,
    isCreatingAttachment:
      createAttachmentMutation.isLoading ||
      createMultiPartAttachmentInitiateMutation.isLoading ||
      createMultiPartAttachmentUploadMutation.isLoading ||
      createMultiPartAttachmentCompleteMutation.isLoading,
    isAttachmentSuccessfullyPublished:
      createAttachmentMutation.isSuccess || createMultiPartAttachmentCompleteMutation.isSuccess,
    showAttachmentsForm:
      (createAttachmentMutation.isIdle || createAttachmentMutation.isLoading) &&
      (createMultiPartAttachmentCompleteMutation.isIdle ||
        createMultiPartAttachmentCompleteMutation.isLoading),
    handleClosePopup,
  };
};

export default useAddAttachmentPopup;
